import { SelectItemGroup } from 'primeng/api';
import { debounceTime, first, distinctUntilChanged, take } from 'rxjs/operators';
import { Subscription } from 'rxjs'

import {
  Component,
  EventEmitter,
  Input,
  Output,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnInit,
  OnChanges,
  SimpleChanges,
  OnDestroy,
} from '@angular/core'

import { Dataset } from '../../../../models/analytics-api/dataset.model';
import { Dimension } from '../../../../models/analytics-api/schema.model';
import { Query } from '../../../../models/analytics-api/query.model';
import {
  QueryResult,
  Slice
} from '../../../../models/analytics-api/query_result.model';

import { escapeRegExp, groupBy } from 'lodash';
import { UntypedFormControl } from '@angular/forms'
import { QueryBuilder } from '../../query-builder'
import { AnalyticsApiService } from '../../../../services/analytics-api/analytics-api.service';
import { MessageService } from '../../../../services/message/message.service';
import { ModalService } from '../../../../services/modal/modal.service';
import { QueryService } from '../../../../services/query/query.service';
import { TrackingService } from '../../../../services/tracking/tracking.service';
import { QueryBuilderModalComponent } from '../query-builder-modal/query-builder-modal.component';
import { MetricFiltersModalComponent } from '../metric-filters-modal/metric-filters-modal.component';

@Component({
  selector: 'query-builder',
  templateUrl: './query-builder.component.html',
  styleUrls: ['./query-builder.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class QueryBuilderComponent implements OnInit, OnChanges, OnDestroy {
  @Input() query: Query
  @Input() result: QueryResult
  @Input() queryBuilder: QueryBuilder
  @Input() disableGroupBy: boolean
  @Input() hideDatasets: boolean
  @Input() hideDimensions: boolean
  @Input() hideDimensionAttributes: boolean
  @Input() hideMetrics: boolean
  @Input() hideAdvancedFilter: boolean
  @Input() queryBuilderId?: number | string;
  @Input() widgetType?: string;

  @Output() queryBuilderChange: EventEmitter<QueryBuilder> = new EventEmitter<QueryBuilder>()

  @Output() runQuery = new EventEmitter<Query>()

  order: any = {}
  dimensionGroupBy: any = {}

  loadingDimensions: boolean
  loadingSchema: boolean

  visibleMetrics: any
  metricFilterControl = new UntypedFormControl()
  metricSubscription: Subscription
  visibleDimensions: any
  dimensionFilterControl = new UntypedFormControl()
  dimensionSubscription: Subscription

  currentView = 'data'

  datasets: Dataset[]
  datasetDropdownOptions: SelectItemGroup[];
  currentDataset: Dataset
  loadingQuery: boolean;

  disallowedWidgetTypes = ['kpi'];

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private analyticsApi: AnalyticsApiService,
    private messageService: MessageService,
    private modalService: ModalService,
    private queryService: QueryService,
    private trackingService: TrackingService
  ) { }

  ngOnInit() {
    this.loadingQuery = true
    this.analyticsApi
      .loadDatasets({ cache: true, skipLoader: true })
      .subscribe(datasets => {
        const groupedByType = groupBy(datasets, 'type')
        const managedByBurt = groupedByType['managed_by_burt'] || []
        const managedByCustomer = groupedByType['managed_by_customer'] || []

        this.datasets = managedByCustomer.concat(managedByBurt);
        this.datasetDropdownOptions = [{ label: '', items: managedByCustomer }, { label: 'Managed Datasets', items: managedByBurt }];
        this.initQuery()
      })

    this.metricSubscription = this.metricFilterControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchString => this.filterMetrics(searchString))

    this.dimensionSubscription = this.dimensionFilterControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchString => this.filterDimensions(searchString))
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.query) {
      this.initQuery()
    }
  }

  initQuery() {
    if (this.datasets && this.datasets.length) {
      if (this.query.dataset) {
        this.resetMetricsFilter()
        this.currentDataset = this.datasets.find(ds => ds.name === this.query.dataset)
        if (this.currentDataset) {
          this.queryBuilder = this.queryService.fetchQueryBuilder(this.queryBuilderId, this.query);
          this.queryBuilderChange.emit(this.queryBuilder)
          this.updateOrderState()
          this.updateGroupByState()
        } else {
          this.messageService.info(`You do not have access to the ${this.query.dataset} dataset. Please contact your account admin to get access.`)
          this.resetQuery()
        }
      } else {
        this.resetQuery()
      }
    } else if (this.datasets && !this.datasets.length) {
      this.messageService.error("You do not have access to any datasets. Please contact your account admin to get access.")
    }
    this.loadingQuery = false

    const queryFilters = this.query.filters || [];
    this.queryService.cacheQueryFilters(queryFilters);
    this.changeDetectorRef.markForCheck()
  }

  resetQuery() {
    this.changeDataset(this.datasets[0])
    this.order = {}
  }

  changeDataset(dataset: Dataset) {
    if (dataset.name !== this.query.dataset) {
      this.loadingSchema = true
      this.resetFilters();
      this.resetMetricsFilter();
      this.resetDimensionFilter();
      this.analyticsApi.loadSchema(dataset.name, { cache: true, skipLoader: true }).subscribe(schema => {
        this.query.dataset = dataset.name
        this.query.schema = schema
        const qb = this.queryService.fetchQueryBuilder(this.queryBuilderId, this.query);
        qb.setDimensions([])
        qb.setMetrics([])

        this.queryBuilder = qb
        this.queryBuilderChange.emit(this.queryBuilder)
        this.filterMetrics('')
        this.filterDimensions('');
        this.currentDataset = this.datasets.find(ds => ds.name === dataset.name)
        this.loadingSchema = false
        this.changeDetectorRef.markForCheck()
        this.runQuery.emit(this.query);
      })
    }
  }

  resetMetricsFilter() {
    this.metricFilterControl.setValue('', { emitEvent: false })
  }

  resetDimensionFilter() {
    this.dimensionFilterControl.setValue('', { emitEvent: false });
  }

  private resetFilters() {
    this.query.filters = [];
    this.query.metricFilters = [];
  }

  filterMetrics(searchString) {
    this.visibleMetrics = null
    if (searchString.length) {
      const regExp = new RegExp(escapeRegExp(searchString), 'i')
      this.visibleMetrics = this.queryBuilder.schema.metrics.filter(metric => (metric.displayName as string).match(regExp))
    }
    this.changeDetectorRef.markForCheck()
  }

  filterDimensions(searchString) {
    this.visibleDimensions = null
    if (searchString.length) {
      const regExp = new RegExp(escapeRegExp(searchString), 'i')
      this.visibleDimensions = this.queryBuilder.schema.dimensions.filter(dimension => (dimension.displayName as string).match(regExp))
    }
    this.changeDetectorRef.markForCheck()
  }

  setDimensions(dimensions) {
    this.queryBuilder.setDimensions(dimensions)
    this.updateGroupByState()
    this.changeDetectorRef.markForCheck()
  }

  propertiesExist(dimensions) {
    return dimensions.flatMap(d => d.properties).length
  }

  setDimensionProperties(dimensionProperties) {
    this.queryBuilder.setDimensionProperties(dimensionProperties)
    this.queryBuilderChange.emit(this.queryBuilder)
  }

  setMetrics(metrics) {
    this.queryBuilder.setMetrics(metrics)
    this.queryBuilderChange.emit(this.queryBuilder)
    this.changeDetectorRef.markForCheck()
  }

  setDates(dates) {
    this.queryBuilder.setDates(dates)
    this.queryBuilderChange.emit(this.queryBuilder)
  }

  public toggleAdvancedFilter(): void {
    this.trackingService.track('Open advanced filter modal')
    this.modalService.open(QueryBuilderModalComponent, {
      closable: false,
      inputs: {
        schema: this.queryBuilder.schema,
        query: this.queryBuilder.query
      },
      outputs: ['modalFiltersClosed'],
      onDeny: () => {
      }
    });

    this.modalService.onOpened.pipe(first()).subscribe(obj => {
      const outputs = obj['outputs'];
      if (outputs['modalFiltersClosed']) {
        outputs['modalFiltersClosed'].pipe(first()).subscribe((data: any) => {
          this.modalService.close();
          if (data.runQuery) {
            this.runQuery.emit(this.query)
          }
        })
      };
    });
  }

  public toggleMetricFilters(): void {
    this.trackingService.track('Open metric filters modal');
    this.modalService.open(MetricFiltersModalComponent, {
      closable: false,
      inputs: {
        schema: this.queryBuilder.schema,
        query: this.queryBuilder.query,
        compatibleMetrics: this.queryBuilder.compatibleMetrics(),
      },
      outputs: ['modalMetricFiltersClosed'],
      onDeny: () => {
      },
    });

    this.modalService.onOpened.pipe(take(1)).subscribe(obj => {
      const outputs = obj['outputs'];
      if (outputs['modalMetricFiltersClosed']) {
        outputs['modalMetricFiltersClosed'].pipe(take(1)).subscribe((data: any) => {
          this.modalService.close();
          if (data.runQuery) {
            this.runQuery.emit(this.query);
          }
        });
      }
    });
  }

  orderByField(field) {
    const order = this.order
    const newOrder = {}
    let name = field.name
    let orderedField
    if (field.type === 'dimension' && !field.useAsDimension) {
      name = `${name}.${name}`
    }

    if (order[name] == null) {
      orderedField = `-${name.replace('.id','')}`
      newOrder[name] = -1
    } else if (order[name] == -1) {
      newOrder[name] = 1
      orderedField = name.replace('.id','')
    }

    this.queryBuilder.setOrder(orderedField)
    this.order = newOrder
  }

  groupByDimension(dimension) {
    const dim = dimension.name
    if (this.dimensionGroupBy[dim] == null || this.dimensionGroupBy[dim]) {
      this.dimensionGroupBy[dim] = false
    } else {
      this.dimensionGroupBy[dim] = true
    }
    const dimensions = this.query.dimensions.filter(dimension => {
      const dim = dimension.name
      return this.dimensionGroupBy[dim] == null || this.dimensionGroupBy[dim]
    })
    this.queryBuilder.setGroupBy(dimensions)
  }

  ngOnDestroy() {
    this.metricSubscription.unsubscribe();
    this.dimensionSubscription.unsubscribe();
  }

  get hasFilters() {
    return !!this.queryBuilder.query.filters && this.queryBuilder.query.filters.some(filter => !!filter.expressions.length);
  }

  get hasIncompatibleDimensionFilters() {
    return this.queryBuilder.query.filters?.some(filter =>
      filter.expressions.some(exp =>
        !this.queryBuilder.editableDimensions().some(dimension => dimension.name === exp.dimension?.name),
      ),
    );
  }

  get hasMetricFilters() {
    return !!this.queryBuilder.query.metricFilters && this.queryBuilder.query.metricFilters.some(filter => !!filter.expressions.length);
  }

  get hasIncompatibleMetricFilters(): boolean {
    return this.queryBuilder.query.metricFilters?.some(metricFilter =>
      metricFilter.expressions.some(exp =>
        !this.queryBuilder.compatibleMetrics().some(metric => metric.name === exp.metric.name),
      ),
    );
  }

  private updateOrderState() {
    this.order = this.query.order.reduce((memo, o) => {
      const parts = o.split('-')
      const metric = parts.at(-1)
      memo[metric] = parts.length == 1 ? 1 : -1
      return memo
    }, {})
  }

  private updateGroupByState() {
    if (this.query.groupBy) {
      this.dimensionGroupBy = this.query.dimensions.reduce((memo, d) => {
        memo[d.name] = this.query.groupBy.includes(d)
        return memo
      }, {})
    }
  }
}
